define((require) => {
    "use strict";

    const EventDispatcher = require("shared/util/EventDispatcher");
    const EditorEvents    = require("shared/services/EditorEvents");

    /**
     * Custom behavior for tracking image model related changes.
     *
     * Intentionally not a `Marionette.Behavior` subclass due to lack of event support.
     *
     * Tracking related to the contained `ImageTagCollection` is handled by the `ImageTagTrackingBehavior`.
     *
     * @see ImageDetailsView
     * @see ImageThumbnailView
     * @see ImageTagTrackingBehavior
     *
     * @class ImageDetailsTrackingBehavior
     */
    return EventDispatcher.extend("ImageDetailsTrackingBehavior", {

        /**
         * Represents the tracking options, defined as an object map/dictionary where the key is
         * the property to observe and the value the `triggerOrigin`.
         *
         * @example
         * ```js
         * imageEditingTracking: {
         *      behaviorClass: ImageDetailsTrackingBehavior,
         *      tracking: {
         *          name: "EditImageNameInputFieldQuickview",
         *          altText: "EditImageAltTextInputFieldQuickview"
         *      }
         *  }
         * ```
         * @typedef {Object.<String, String>} TrackingOptions
         */

        /**
         * Constructor.
         *
         * @param {Object} options The options defined for `this` behavior.
         * @param {TrackingOptions} options.tracking The `TrackingOptions`.
         * @param {Backbone.View} view The associated view instance.
         */
        constructor({tracking}, view) {
            this._tracking = tracking;
            this._view = view;
            this._eventCache = new Map();
        },

        /**
         * Invoked via the Marionette behavior lifecycle.
         */
        onShow() {
            this._model = this._view.model;
            this.listenTo(this._model, "sync", this._modelSyncedHandler);

            Object.keys(this._tracking).forEach((property) => {
                this.listenTo(this._model, `change:${property}`, this._modelChangeHandler);
            });
        },

        /**
         * Invoked via the Marionette behavior lifecycle.
         */
        onClose() {
            this.stopListening();

            // In case we're closed before the "sync" event arrives - make sure to track any pending events.
            if (this._eventCache.size > 0) {
                this._trackEvents();
            }
        },

        /**
         * Invoked on model changes.
         *
         * @see #onShow
         *
         * @param {ImageModel} model The current `ImageModel`.
         * @param {*} value The value of the property that changed.
         * @param {Object} options Any options passed with the event.
         * @param {Object} options.stickitChange Stickit options containing the needed view reference, if stickit caused this change.
         * @param {Object} options.editBehaviorChange `EditBehavior` options containing the needed view reference, if `EditBehavior` caused this change.
         */
        _modelChangeHandler(model, value, {stickitChange, editBehaviorChange}) {
            const {changed} = model;
            // Determine the view causing the change - based on either stickit or the EditBehavior
            const view = (stickitChange && stickitChange.view) || (editBehaviorChange && editBehaviorChange.view);

            // Ensure that the change origins from the associated view - we may have more than one view modifying the same model instance.
            if (view === this._view) {
                Object.keys(changed).forEach((property) => this._eventCache.set(property, changed[property]));
            }
        },

        _modelSyncedHandler() {
            this._trackEvents();
        },

        /**
         * Tracks all events currently in the "event cache", and clears the cache.
         */
        _trackEvents() {
            this._eventCache.forEach((value, property) => {
                const triggerOrigin = this._tracking[property];
                const eventProperties = {
                    imageId: this._model.get("id"),
                    imageName: this._model.get("name"),
                    triggerOrigin: triggerOrigin,
                    updatedProperty: property,
                    updatedValue: value
                };

                EditorEvents.trigger(EditorEvents.constants.UPDATE_IMAGE, eventProperties);
            });

            this._eventCache.clear();
        }

    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/imageLibrary/ImageDetailsTrackingBehavior.js